
<script setup>

import { ref, onMounted, computed, watch, watchEffect, nextTick } from 'vue'

import { useElementVisibility, useElementBounding, useMediaQuery } from '@vueuse/core'

import { useRouteQuery } from '@vueuse/router'

import { useRouter } from 'vue-router'

import { tabs } from '../../state/tabs'

import { demographicStatsReference } from '../../state/demographic-stats'

import { populationHealthReference } from '../../state/metric-stats'

import mockupUrl from 'url:../../images/pop-health-data-mockup.png'

import amsUrl from 'url:../../images/AMS-label-full-color-blue-glow.png'

import logoUrl from 'url:../../images/logo.svg'

import zipCode from '../../state/zip-code'

import place from '../../state/place'

import demographicStats from '../../state/demographic-stats'

import metricStats from '../../state/metric-stats'

import healthDataFacts from '../../state/health-stats'

import history from '../../state/history'

import DemographicDivider from '../icons/demographic-divider.vue'

import SectionDivider from '../icons/section-divider.vue'

import Header from '../partials/header.vue'

import Footer from '../partials/footer.vue'

import Button from '../partials/button.vue'

import Demographic from '../partials/demographic.vue'

import PopulationHealth from '../partials/population-health.vue'

import HealthDataCards from '../partials/health-data-cards.vue'

import CommunityImpactCards from '../partials/community-impact-cards.vue'

import OutbreakAlert from '../partials/outbreak-alert.vue'

import References from '../partials/references.vue'

import communityImpactFacts from '../../data/community-impact.json'

import references from '../../data/references.json'

const isSticky = ref( false )

const navigationSection = ref( null )

const endSection = ref( null )

const tabRefs = ref( [] )

const zipCodeInput = ref( null )

const router = useRouter()

const currentUrl = useRouteQuery( 'zip-code' )

const healthCardBreakpoint = useMediaQuery( '(min-width: 768px)' )

const activeTab = computed( () => tabs.value.find( tab => tab.active ) )

const activeTabCards = computed( () => healthDataFacts.value.filter( fact => fact.tab === activeTab.value.id ) )

const tabHasUnseenUpdatedCards = tab =>

  ! history.seen( zipCode.value, tab.id ) &&
  
  healthDataFacts.value.filter( fact => fact.tab === tab.id && fact.moreInfo.updatedAt?.alert ).length;

const { top: navPosition, height: navHeight } = useElementBounding( navigationSection )

const { top: endSectionPosition } = useElementBounding( endSection )

const handleTab = selectedTab => {

  tabs.value.forEach( tab => tab.active = tab.id === selectedTab.id )
  
  tabRefs.value.find( ( btn ) => btn.id === selectedTab.id ).scrollIntoView( { behavior: 'smooth', block: "nearest", inline: "center" } )

}

const activeStyles = computed( () => {

  return {
    
    border: activeTab.value.styles.border,

    bgColor: activeTab.value.styles.bgColor 

  }

} )

const handleScroll = () => isSticky.value = navPosition.value <= 0 && endSectionPosition.value > navHeight.value

const handleSubmit = () => {

  router.push( { query: { 'zip-code': zipCodeInput.value } } )

  zipCodeInput.value = null

}

const setShowMoreInfo = ( selectedCard, cards ) => {

  cards.filter( card => card.id !== selectedCard.id ).map(card => card.moreInfo.active = false )

  cards.filter( card => card.id === selectedCard.id ).map(card => card.moreInfo.active = !card.moreInfo.active )

}

onMounted( () => {

  tabRefs.value.forEach( ( tabRef, index ) => tabs.value[ index ].visible = useElementVisibility( tabRef, { rootMargin: '50px', threshold: 0.99 } ) )
  
  window.addEventListener( 'scroll', handleScroll ) 

} )

watch( () => place.exists, ( exists ) => {

  if ( !exists ) return

  nextTick( () => tabRefs.value.forEach( ( tabRef, index ) => { 
    
    tabs.value[ index ].visible = useElementVisibility( tabRef, { rootMargin: '50px', threshold: 0.99 } )

  } ) )

}, { immediate: true } )

watch( activeTab, ( newTab, oldTab ) => history.saw( zipCode.value, oldTab?.id ) );

watchEffect( () => {

  if ( healthCardBreakpoint.value ) {

    nextTick( () => tabRefs.value.forEach( ( tabRef, index ) => { 
      
      tabs.value[ index ].visible = useElementVisibility( tabRef, { rootMargin: '50px', threshold: 0.99 } )
      
    } ) )
    
  }

} )

watchEffect( () => {

  if ( activeTabCards.value ) {

    activeTabCards.value.map( card => card.moreInfo.active = false )

  }

  if ( zipCode.value ) {

    activeTabCards.value.map( card => card.moreInfo.active = false )

  }

  zipCode.value = currentUrl.value

} )

const navigateToForm = () => {

  router.push( {

    path: '/form',

    query: {

      'zip-code': zipCode.value,

    },

  } )

}

</script>

<template>

  <Header>

    <div class="flex items-center">

      <Button
      
        v-if="place.exists" @click="navigateToForm()"
      
        class="sm:absolute sm:right-0 px-5 sm:px-10 text-sm sm:text-base text-white bg-blue-primary hover:bg-blue-prussian-hover z-20">

        download my report

      </Button>

    </div>

  </Header>

  <main class="text-white" v-if="place.ready">

    <section class="bg-linear-to-t from-blue-primary to-blue-prussian px-6 sm:px-8 pb-6">

      <div class="max-w-screen-xl mx-auto py-8">

        <div class="sm:pt-0 pt-4 flex flex-col items-start gap-4 sm:flex-row sm:justify-between sm:items-center">

          <div v-if="place.exists">

            <p class="font-bold uppercase">your area</p>

            <h1 class="bg-linear-to-r from-[#45C5E3] to-[#BFD630] inline-block text-transparent bg-clip-text font-bold text-2xl sm:text-3xl capitalize">
              
              <span>{{ place.county }}, {{ place.stateAbbr }} ({{ zipCode }})</span>

            </h1>

            <p class="max-w-lg mt-4 italic text-xs">
              
              Note:
              
              State or regional data may be used when county data is unavailable.

              National averages are calculated for each data point given the county, state, or regional data available.
              
              National averages are not part of the original source data.
            
            </p>

          </div>

          <div v-else>

            <p class="bg-linear-to-r from-[#45C5E3] to-[#BFD630] inline-block text-transparent bg-clip-text font-bold uppercase">please try again</p>

            <h1 class="font-bold text-2xl sm:text-3xl">
              
              Your ZIP code "{{ zipCode }}" was not found.
            
            </h1>

          </div>

          <form @submit.prevent="handleSubmit">

            <div class="relative">

              <input id="zipCode" name="zip-code" type="text" pattern="^\d{5}$" maxlength="5" placeholder="" required v-model="zipCodeInput"

                class="bg-white peer w-52 md:w-64 h-12 px-3 pt-5 text-neutral-700 rounded-md border-0 outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-green-bitter-lemon focus-visible:ring-offset-blue-prussian" />

              <label for="zipCode" class="flex items-center absolute top-0 left-0 text-base h-12 mx-3 italic text-neutral-400 pointer-events-none origin-top-left transform -translate-y-1 scale-75 transition-transform peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:-translate-y-1 peer-focus:scale-75">

                Enter another ZIP code

              </label>

            </div>

          </form>

        </div>

      </div>

      <div v-if="place.exists" class="max-w-screen-xl mx-auto">

        <SectionDivider />

      </div>

      <div v-if="place.exists" class="max-w-screen-xl mx-auto py-8">

        <div class="grid xl:grid-cols-3 gap-8">

          <div class="flex flex-col">

            <h2 class="font-bold text-xl mb-4">
              
              Demographics
            
              <sup class="text-xs -ml-0.5">{{ demographicStatsReference }}</sup>

            </h2>

            <DemographicDivider class="stroke-4"/>

            <div v-for="( stat, index ) in demographicStats" :key="stat.id">

              <Demographic :stat="stat" />

              <DemographicDivider v-if="index < demographicStats.length - 1" class="stroke-3 opacity-50"/>
              
            </div>

          </div>

          <div class="xl:col-span-2">

            <h2 class="font-bold text-xl mb-4">
              
              Population Health Metrics
            
              <sup class="text-xs -ml-0.5">{{ populationHealthReference }}</sup>
            
            </h2>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">

              <PopulationHealth v-for="metric in metricStats" :key="metric.id" :metric="metric" />

            </div>

          </div>

        </div>

      </div>

    </section>

    <section v-if="place.exists && healthCardBreakpoint" class="bg-blue-primary px-6 sm:px-8 z-20" :class="{ 'sticky top-0' : isSticky }" ref="navigationSection">
    
      <div class="max-w-screen-xl mx-auto">

        <div class="flex justify-between pt-4 items-end gap-8">

          <div class="flex overflow-x-auto whitespace-nowrap relative -bottom-[5px]">
            
            <button

              v-for="tab in tabs"

              :key="tab.id"

              :id="tab.id"
              
              ref="tabRefs"

              :disabled="tab.active"

              @click="handleTab( tab )"

              class="text-sm px-2 py-2 lg:px-4 lg:text-base rounded-t-md border-b-5"

              :class="[

                tab.visible ? 'text-neutral-300' : 'text-neutral-500', 

                tab.active ? `bg-grey-background border-b-grey-background text-[#373737]! font-bold` : `hover:text-white cursor-pointer ${activeStyles.border} ${tab.styles.borderHover}`,

              ]"

            >
            
              <div class="flex gap-x-2">

                <div v-if="tabHasUnseenUpdatedCards( tab )" class="flex items-center px-2 text-xs text-white bg-[#69a03b] rounded-md">New</div>

                <span>{{ tab.label }}</span>

              </div>

            </button>

          </div>

          <Button size="sm" @click="navigateToForm()"
          
            class="hidden md:inline-block mb-4 text-blue-primary bg-green-bitter-lemon hover:bg-green-rio whitespace-nowrap">

            download full report

          </Button>

        </div>

      </div>

      <div class="sm:-mx-8 -mx-6 border-b-5" :class="`${ activeStyles.border }`"></div>

    </section>

    <section v-if="place.exists" class="overflow-hidden bg-grey-background px-6 sm:px-8 pb-12 md:pb-16 xl:pb-20">

      <div class="max-w-screen-xl mx-auto">

        <div class="my-6 md:my-8 xl:my-10">

          <!-- For future alert bar. -->

        </div>

        <div class="grid lg:grid-cols-3 sm:grid-cols-2 gap-8 md:gap-x-12 md: gap-y-18" v-if="healthCardBreakpoint">

          <HealthDataCards v-for="card in activeTabCards" @click="setShowMoreInfo( card, activeTabCards )" :key="card.id" :card="card" :bgColor="`${ activeStyles.bgColor }`" />
          
        </div>

        <div class="flex flex-col gap-16" v-else>

          <div>

            <h2 class="text-neutral-900 font-bold text-2xl mb-4 capitalize">respiratory</h2>

            <div class="grid gap-x-8 gap-y-16 sm:grid-cols-2">

              <HealthDataCards v-for="card in healthDataFacts.filter( fact => fact.tab === 'respiratory' )" @click="setShowMoreInfo( card, healthDataFacts )" :key="card.id" :card="card" :bgColor="`${ tabs.filter(tab => tab.id === 'respiratory').map(tab => tab.styles.bgColor) }`" />
              
            </div>

          </div>

          <div>

            <h2 class="text-neutral-900 font-bold text-2xl mb-4 capitalize">gastrointestinal</h2>

            <div class="grid gap-x-8 gap-y-16 sm:grid-cols-2">

              <HealthDataCards v-for="card in healthDataFacts.filter( fact => fact.tab === 'gastrointestinal' )" @click="setShowMoreInfo( card, healthDataFacts )" :key="card.id" :card="card" :bgColor="`${ tabs.filter(tab => tab.id === 'gastrointestinal').map(tab => tab.styles.bgColor) }`" />
              
            </div>

          </div>

          <div>

            <h2 class="text-neutral-900 font-bold text-2xl mb-4 capitalize">meningitis</h2>

            <div class="grid gap-x-8 gap-y-16 sm:grid-cols-2">

              <HealthDataCards v-for="card in healthDataFacts.filter( fact => fact.tab === 'meningitis' )" @click="setShowMoreInfo( card, healthDataFacts )" :key="card.id" :card="card" :bgColor="`${ tabs.filter(tab => tab.id === 'meningitis').map(tab => tab.styles.bgColor) }`" />
              
            </div>

          </div>

          <div>

            <h2 class="text-neutral-900 font-bold text-2xl mb-4 capitalize">tropical fever</h2>

            <div class="grid gap-x-8 gap-y-16 sm:grid-cols-2">

              <HealthDataCards v-for="card in healthDataFacts.filter( fact => fact.tab === 'tropical-fever' )" @click="setShowMoreInfo( card, healthDataFacts )" :key="card.id" :card="card" :bgColor="`${ tabs.filter(tab => tab.id === 'tropical-fever').map(tab => tab.styles.bgColor) }`" />
              
            </div>

          </div>

          <div>

            <h2 class="text-neutral-900 font-bold text-2xl mb-4 capitalize">bloodstream infections</h2>

            <div class="grid gap-x-8 gap-y-16 sm:grid-cols-2">

              <HealthDataCards v-for="card in healthDataFacts.filter( fact => fact.tab === 'bloodstream-infections' )" @click="setShowMoreInfo( card, healthDataFacts )" :key="card.id" :card="card" :bgColor="`${ tabs.filter(tab => tab.id === 'bloodstream-infections').map(tab => tab.styles.bgColor) }`" />
              
            </div>

          </div>

        </div>

        <div ref="endSection">
          
          <h2 class="font-bold text-xl text-blue-primary mt-18 mb-6">Community Impact of Syndromic Testing</h2>

          <div class="grid lg:grid-cols-4 sm:grid-cols-2 gap-8 xl:gap-16">

            <CommunityImpactCards v-for="fact in communityImpactFacts" :key="fact.id" :heading="fact.heading" :text="fact.text" :reference="fact.reference" />

          </div>

        </div>

      </div>

    </section>

    <section v-if="place.exists" class="bg-blue-primary px-6 sm:px-8 py-8 lg:py-4">

      <div class="max-w-screen-xl mx-auto flex flex-col lg:h-26 lg:flex-row gap-6 items-center justify-between">

        <div class="hidden lg:block">

          <img :src="mockupUrl" alt="bioMérieux Population Health Data Mockup" class="w-64" />

        </div>

        <div class="text-center lg:text-left">

          <h2 class="font-bold text-2xl lg:text-3xl">Download my full custom report.</h2>

        </div>

        <Button @click="navigateToForm()" class="text-blue-primary bg-green-bitter-lemon hover:bg-green-rio px-10">

          download <span class="inline-block lg:hidden xl:inline-block">full report</span>

        </Button>

      </div>

    </section>

    <section class="bg-white px-6 sm:px-8 sm:py-32 py-16">

      <div class="max-w-screen-lg mx-auto">

        <div class="grid md:grid-cols-2 gap-8 md:gap-0">

          <div class="flex flex-col gap-2">

            <p class="font-bold text-blue-primary text-2xl">The fight against antimicrobial resistance starts here.</p>

            <p class="text-neutral-700 text-lg">

              Our diagnostic solutions serve as frontline defenders against the

              growing threat of drug resistance by swiftly and accurately

              identifying potential pathogens to help inform effective treatment
              
              options.

            </p>

          </div>

          <div class="flex items-center justify-center">

            <img :src="amsUrl" alt="AMS Logo" class="w-90"/>

          </div>

        </div>

      </div>

    </section>
  
    <section class="bg-grey-background px-6 sm:px-8 py-12 md:py-16">

      <div class="max-w-screen-xl mx-auto">

        <References :references="references" v-if="place.exists" />

        <References :references=[] v-else />

      </div>

    </section>

  </main>

  <div v-else class="w-screen h-screen bg-linear-to-t from-blue-primary to-blue-prussian"></div>

  <Footer>

    <template #logo>

      <img class="w-[66px] h-[66px] -mb-[22px] mx-auto sm:w-[124px] sm:h-[124px] sm:-mb-[40px] transition-[width,height] duration-300" :src="logoUrl" alt="bioMérieux logo" />

    </template>

    <template #note>

      <p class="mb-5 font-bold">Product availability varies by country. Consult your bioMérieux representative.</p>

    </template>

  </Footer>

</template>
